import { UNLEASH_CLIENT_KEY, UNLEASH_ENABLED, UNLEASH_URL } from './env';

/**
 * Creates an Unleash client configuration object.
 * Implements graceful degradation if Unleash is not configured or disabled.
 *
 * @param {Object} configOptions - Configuration options
 * @param {string} configOptions.userId - Optional user ID for tracking
 * @returns {Object} Unleash configuration object
 */
export const createUnleashConfig = (configOptions = {}) => {
  const { userId = null } = configOptions;

  // Return minimal config if Unleash is disabled
  if (!UNLEASH_ENABLED) {
    return {
      enabled: false,
    };
  }

  // Return minimal config if required parameters are missing
  if (!UNLEASH_URL || !UNLEASH_CLIENT_KEY) {
    // eslint-disable-next-line no-console
    console.warn(
      'Unleash configuration incomplete. Feature flags will be disabled.',
      { url: !!UNLEASH_URL, clientKey: !!UNLEASH_CLIENT_KEY },
    );
    return {
      enabled: false,
    };
  }

  // Return full configuration
  return {
    url: UNLEASH_URL,
    clientKey: UNLEASH_CLIENT_KEY,
    appName: 'sales-cockpit',
    refreshInterval: 15000, // 15 seconds
    sessionId: userId || 'anonymous',
    // Conservative strategy: features enabled by default, disabled only if explicitly toggled off
    fetch: (url, fetchOptions) =>
      fetch(url, {
        ...fetchOptions,
        mode: 'cors',
        credentials: 'omit',
      }),
  };
};

/**
 * Checks if Unleash is properly configured and enabled
 * @returns {boolean} True if Unleash is configured and enabled
 */
export const isUnleashEnabled = () =>
  UNLEASH_ENABLED && !!UNLEASH_URL && !!UNLEASH_CLIENT_KEY;

/**
 * Safe flag retrieval with fallback to true (enables feature by default during initialization)
 * @param {boolean} flagEnabled - The flag status from Unleash
 * @returns {boolean} Safe flag value
 */
export const getFlagValue = flagEnabled => {
  // If flag status is explicitly false, return false; otherwise return true
  return flagEnabled !== false;
};
